import React from 'react';
import { useBreakpoints } from 'hooks/useBreakpoints';

// Helper function to generate dynamic ranges
function generateDynamicRanges(breaks) {
  if (!breaks || breaks.length < 2) return [];

  // Check if all breaks are integers
  const allIntegers = breaks.every(b => Number.isInteger(b));

  const ranges = [];
  for (let i = 0; i < breaks.length - 1; i++) {
    const start = breaks[i];
    const next = breaks[i + 1];
    const diff = next - start;
    let end;

    if (allIntegers) {
      // For integers — subtract 5% of step or at least 1
      const decrement = Math.max(Math.ceil(diff * 0.05), 1);
      end = next - decrement;
      if (end <= start) end = next - 1; // fallback safety
    } else {
      // For floats — shift about 1/3 toward the next break
      const decrement = diff * 0.33;
      end = +(start + diff - decrement).toFixed(2);
    }

    ranges.push(`${start} to ${end}`);
  }
  return ranges;
}

const CircleLegend = ({
  showIndicator3,
  showIndicator4,
  showIndicator5,
  indicator3Breaks = [],
  indicator4Breaks = [],
  indicator5Breaks = [],
  colors,
  setMapConfigData
}) => {
  const { breakpoints } = useBreakpoints();
  //=========== Mobile View =============
  const isMobile = breakpoints.down('md');
  const indicator_3_name = setMapConfigData?.indicators[0]?.label;
  const indicator_4_name = setMapConfigData?.indicators[1]?.label;
  const indicator_5_name = setMapConfigData?.indicators[2]?.label;
  const indicator_3_sub_name = setMapConfigData?.indicators[0]?.subIndicator?.label;
  const indicator_4_sub_name = setMapConfigData?.indicators[1]?.subIndicator?.label;
  const indicator_5_sub_name = setMapConfigData?.indicators[2]?.subIndicator?.label;

  const hasIndicator = showIndicator3 || showIndicator4 || showIndicator5;
  const hasLabel = indicator_3_name || indicator_4_name || indicator_5_name;
  if (!hasIndicator || !hasLabel) return null;

  // Validation function for numeric breaks
  const isValidBreaks = (arr) =>
    Array.isArray(arr) && arr.every(b => !isNaN(parseFloat(b)));

  // Vertical rendering for circles
  const renderLegendSVG = (breaks, color) => {
    if (!isValidBreaks(breaks) || !breaks || breaks.length === 0) {
      return (
        <p className='text-center fw-bold'>No Data Available</p>
      );
    }

    const maxRadii = [2, 5, 10, 15, 20]; // largest to smallest
    let radii = [];
    let labels = [];

    if (breaks.length >= 5) {
      radii = maxRadii;
      // Use the helper function to generate dynamic ranges for all breaks
      labels = generateDynamicRanges(breaks);

      // Add the final range: from second-to-last to last break
      const secondLast = breaks[breaks.length - 2];
      const last = breaks[breaks.length - 1];
      labels.push(`${secondLast} to ${last}`);
    } else {
      radii = [20, 15, 10, 5, 2].slice(0, breaks.length);
      labels = breaks.map(val => `${val}`);
    }

    return (
      <div className="d-flex flex-column align-items-center gap-3 mb-3">
        {radii.map((radius, i) => (
          <div key={i} className="d-flex flex-column align-items-center">
            <div
              style={{
                width: radius * 2,
                height: radius * 2,
                borderRadius: '50%',
                backgroundColor: color,
                opacity: 0.4,
                border: `2px solid ${color}`
              }}
            ></div>
            <small className="text-center mt-1 mb-2" style={{ fontSize: '0.75rem' }}>
              {labels[i]}
            </small>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {showIndicator3 && indicator_3_name && (
        <div className="legend-box bg-white border rounded shadow p-3 mt-2 gap-4" style={{ flex: "1 1 auto", width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: "0.5rem" }}>
          <div className='mt-3'>
            {renderLegendSVG(indicator3Breaks, colors?.[colors.length - 4])}
          </div>
          <div className="fw-bold mt-2">
            {indicator_3_name}
            {indicator_3_sub_name && (
              <span className="text-success"> | [{indicator_3_sub_name}]</span>
            )}
          </div>
        </div>
      )}

      {showIndicator4 && indicator_4_name && (
        <div className="legend-box bg-white border rounded shadow p-3 mt-2" style={{ flex: "1 1 auto", width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: "0.5rem" }}>
          <div className='mt-3'>
            {renderLegendSVG(indicator4Breaks, colors?.[colors.length - 5])}
          </div>
          <div className="fw-bold mt-2">
            {indicator_4_name}
            {indicator_4_sub_name && (
              <span className="text-success"> | [{indicator_4_sub_name}]</span>
            )}
          </div>
        </div>
      )}

      {showIndicator5 && indicator_5_name && (
        <div className="legend-box bg-white border rounded shadow p-3 mt-2" style={{ flex: "1 1 auto", width: isMobile ? "calc(100% - 1rem)" : "calc(20%)", margin: "0.5rem" }}>
          <div className='mt-3'>
            {renderLegendSVG(indicator5Breaks, colors?.[colors.length - 6])}
          </div>
          <div className="fw-bold mt-2">
            {indicator_5_name}
            {indicator_5_sub_name && (
              <span className="text-success"> | [{indicator_5_sub_name}]</span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CircleLegend;